

<template>
  <div class="card-history" @click="!loading_service ? setFilters() : ''">
    <b-skeleton class="skeleton-loading-service" v-if="loading_service"></b-skeleton>

    <!-- Accent bar con color de red -->
    <div class="accent-bar" :style="{ background: accentGradient }"></div>

    <div class="card-body-inner">

      <!-- Fila superior: chip red + acciones -->
      <div class="top-row">
        <!-- Modo edición de título -->
        <template v-if="show_input">
          <b-form-input
            v-model="title"
            class="title-input"
            maxlength="250"
            autocomplete="off"
            @click.stop
          ></b-form-input>
        </template>
        <template v-else>
          <div class="network-chip" :style="chipStyle">
            <div class="chip-dot" :style="{ background: getColor(i.network, true) }">
              <img :src="getIconWhite(i.network)" width="10" height="10" :alt="i.network" />
            </div>
            {{ nameNetworks(i.network, false) }}
          </div>
        </template>

        <div class="actions" @click.stop>
          <template v-if="show_input">
            <button class="action-btn save" @click.stop="changeTitle(i.uuid)" :title="$t('creator.save')">✓</button>
            <button class="action-btn cancel" @click.stop="show_input = false" :title="$t('creator.cancel')">✕</button>
          </template>
          <template v-else>
            <button class="action-btn edit" @click.stop="editTitleAction(i)" :title="$t('filters.editName')">
              <feather-icon icon="EditIcon" size="13" />
            </button>
            <button class="action-btn del" @click.stop="deleteHistory(i.uuid)" :title="$t('lists.delete')">
              <feather-icon icon="TrashIcon" size="13" />
            </button>
          </template>
        </div>
      </div>

      <!-- Título personalizado (si existe) -->
      <div v-if="i.title && !show_input" class="custom-title">{{ i.title }}</div>

      <!-- Filtros -->
      <div class="filters-row" v-if="!show_input">
        <template v-if="filtersList.length > 0">
          <span class="filters-text">{{ filtersList.slice(0, 1).join('') }}</span>
          <span v-if="filtersList.length > 1" class="filters-badge">{{ $t('filters.filtersMore', { count: filtersList.length - 1 }) }}</span>
          <div class="filters-tooltip" v-if="filtersList.length > 1">
            <div v-for="(f, idx) in filtersList" :key="idx" class="tooltip-item">{{ f }}</div>
          </div>
        </template>
        <span v-else class="no-data">{{ $t('filters.noFilters') }}</span>
      </div>

      <!-- Fila inferior: resultados + fecha/hora -->
      <div class="bottom-row" v-if="!show_input">
        <span class="results" v-if="i.request.total_results">
          {{ $t('filters.resultsCount', { count: getFormat(i.request.total_results) }) }}
        </span>
        <span class="no-data" v-else>{{ $t('filters.noResults') }}</span>
        <div class="datetime">
          {{ getDateShort(i.created_at) }}<br/>{{ getTimeStr(i.created_at) }}
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {
  BFormInput,
  BSkeleton,
} from 'bootstrap-vue'
import services_search from '@/services/search';
import { getFormat, nameNetworks } from '@/libs/utils/formats';
import { getDatev2 } from '@/libs/utils/times';
import { getColor, getIconWhite } from '@/libs/utils/icons';

export default {
  name: 'cardHistorySearch',
  components: {
    BFormInput,
    BSkeleton,
  },
  data () {
    return {
      getFormat,
      getDatev2,
      getColor,
      getIconWhite,
      nameNetworks,
      title: '',
      show_input: false,
    }
  },
  props: {
    i: {
      type: Object,
      default: () => ({})
    },
    loading_service: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    filtersList() {
      const label = this.i.request && this.i.request.filters_label_used;
      if (!label) return [];
      return label.split(' • ').map(s => s.trim()).filter(Boolean);
    },
    accentGradient() {
      const map = {
        instagram: 'linear-gradient(90deg,#e1306c,#f77737)',
        youtube:   'linear-gradient(90deg,#c4302b,#ff0000)',
        tiktok:    'linear-gradient(90deg,#010101,#69c9d0)',
        facebook:  'linear-gradient(90deg,#1877f2,#42a5f5)',
        twitter:   'linear-gradient(90deg,#1da1f2,#71c9f8)',
        linkedin:  'linear-gradient(90deg,#0077b5,#00a0dc)',
        twitch:    'linear-gradient(90deg,#9146ff,#bf94ff)',
        spotify:   'linear-gradient(90deg,#1db954,#1ed760)',
        pinterest: 'linear-gradient(90deg,#bd081c,#e60023)',
        snapchat:  'linear-gradient(90deg,#fffc00,#ffd700)',
        blog:      'linear-gradient(90deg,#6b7280,#9ca3af)',
      };
      return map[this.i.network] || 'linear-gradient(90deg,#7C3AED,#a78bfa)';
    },
    chipStyle() {
      const bgMap = {
        instagram: { bg: '#fff0f5', color: '#c13584' },
        youtube:   { bg: '#fff0f0', color: '#c4302b' },
        tiktok:    { bg: '#f0fafa', color: '#010101' },
        facebook:  { bg: '#eff6ff', color: '#1877f2' },
        twitter:   { bg: '#eff9ff', color: '#1da1f2' },
        linkedin:  { bg: '#eff8ff', color: '#0077b5' },
        twitch:    { bg: '#f5f0ff', color: '#9146ff' },
        spotify:   { bg: '#f0fdf4', color: '#1db954' },
        pinterest: { bg: '#fff0f0', color: '#bd081c' },
        snapchat:  { bg: '#fffde7', color: '#b8a600' },
        blog:      { bg: '#f3f4f6', color: '#374151' },
      };
      const s = bgMap[this.i.network] || { bg: '#f5f0ff', color: '#7C3AED' };
      return { background: s.bg, color: s.color };
    },
  },
  methods: {
    setFilters() {
      this.$emit('set_filters', this.i)
    },
    deleteHistory(search_uuid) {
      // Optimistic: quita de UI de inmediato
      this.$emit('delete_history', search_uuid)
      services_search.deleteHistorySearch(search_uuid).catch(() => {
        // Si falla, recarga para restaurar estado
        this.$emit('reload_history')
      })
    },
    editTitleAction(filters) {
      this.title = filters.title ? filters.title : filters.network;
      this.show_input = true;
    },
    changeTitle(searcher_uuid) {
      const title = this.title;
      services_search.changeTitleHistory(searcher_uuid, { title }).then((response) => {
        this.show_input = false;
        this.$emit('update_history', searcher_uuid, response)
      })
    },
    getDateShort(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    },
    getTimeStr(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const h = String(d.getHours()).padStart(2, '0');
      const m = String(d.getMinutes()).padStart(2, '0');
      return `${h}:${m} h`;
    },
  }
}
</script>

<style lang="scss" scoped>
.card-history {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 6px 20px rgba(124, 58, 237, 0.10);
    transform: translateY(-2px);
  }
}

.accent-bar {
  height: 3px;
  flex-shrink: 0;
}

.card-body-inner {
  padding: 12px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

/* Top row */
.top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
}

.network-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px 3px 3px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.chip-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Actions */
.actions {
  display: flex;
  gap: 3px;
  flex-shrink: 0;
}

.action-btn {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: none;
  background: transparent;
  color: #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  font-size: 13px;
  font-weight: 700;

  &.edit:hover { background: #ede9fe; color: #7C3AED; }
  &.del:hover  { background: #fee2e2; color: #ef4444; }
  &.save:hover { background: #d1fae5; color: #059669; }
  &.cancel:hover { background: #f3f4f6; color: #374151; }
}

/* Custom title */
.custom-title {
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  line-height: 1.3;
}

/* Filters */
.filters-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 18px;

  &:hover .filters-tooltip {
    opacity: 1;
    pointer-events: auto;
  }
}

.filters-text {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.filters-badge {
  display: inline-block;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
}

.filters-tooltip {
  opacity: 0;
  pointer-events: none;
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  background: #1e1b4b;
  color: white;
  font-size: 10px;
  line-height: 1.6;
  padding: 8px 10px;
  border-radius: 8px;
  z-index: 100;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: opacity 0.15s;
  min-width: 150px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 12px;
    border: 5px solid transparent;
    border-top-color: #1e1b4b;
  }
}

.tooltip-item {
  white-space: nowrap;
}

/* No data */
.no-data {
  font-size: 11px;
  color: #d1d5db;
  font-style: italic;
}

/* Bottom row */
.bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: auto;
}

.results {
  font-size: 11px;
  font-weight: 700;
  color: #7C3AED;
}

.datetime {
  font-size: 10px;
  color: #9ca3af;
  text-align: right;
  line-height: 1.4;
  flex-shrink: 0;
}

/* Edit input */
.title-input {
  font-size: 12px;
  height: 28px;
  padding: 2px 8px;
  flex: 1;
}

/* Skeleton */
.skeleton-loading-service {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: #8a89891a;
  z-index: 1;
}
</style>
